import { useCallback, useEffect, useState } from "react";
import EmployeeSelect from "../../common/EmployeeSelect";
import { formatEmployeeLabel } from "../../common/employeeUtils";
import {
  cancelEncashment,
  decideEncashment,
  fetchEncashmentContext,
  fetchEncashmentRequests,
  quoteEncashment,
  retryEncashmentPayroll,
  runYearEndEncashment,
  submitEncashment,
} from "../../../Moduels/leave/encashmentApi";
import "./LeaveEncashment.css";

function money(value) {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(amount);
}

function days(value) {
  if (value == null || value === "") return "0";
  return String(value).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function label(value) {
  return String(value || "—").replace(/_/g, " ");
}

function requestStatus(row) {
  if (row.status === "pending_hr" || row.status === "pending_manager") return "Waiting for approval";
  if (row.status === "approved") return "Encashment created";
  return label(row.status);
}

function payrollLabel(status) {
  if (status === "setup_required") return "Needs setup";
  if (status === "queued") return "Waiting for payroll";
  if (status === "pending") return "In payroll";
  if (status === "processed") return "Paid";
  if (status === "failed") return "Not posted";
  if (status === "none") return "Not sent";
  return label(status);
}

function isPackageSetupMessage(text) {
  return /leave encashment earning component|LEAVE_ENCASH/i.test(text || "");
}

function rowNotice(row) {
  if (row.status === "cancelled") {
    return {
      tone: "warn",
      text: "This encashment was cancelled. The amount was not added to the payslip.",
    };
  }
  if (row.payroll_status === "queued") {
    return {
      tone: "warn",
      text: "No open pay period yet. This amount will show on the payslip as Leave Encashment when payroll is run. It is not part of the salary package.",
    };
  }
  if (row.payroll_status === "setup_required" || isPackageSetupMessage(row.error_message)) {
    return {
      tone: "warn",
      text: "Approved leave encashment is added on this employee's payslip only. It is not part of the salary package.",
    };
  }
  if (row.rejection_reason) return { tone: "bad", text: row.rejection_reason };
  if (row.payroll_status === "failed" && row.error_message) return { tone: "bad", text: row.error_message };
  if (row.error_message) return { tone: "bad", text: row.error_message };
  return null;
}

export default function LeaveEncashmentPage() {
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [daysToEncash, setDaysToEncash] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState({ items: [], total: 0, limit: 20, offset: 0 });
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [busyId, setBusyId] = useState("");
  const [confirmRun, setConfirmRun] = useState(false);
  const [runSummary, setRunSummary] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBasis, setShowBasis] = useState(false);

  const loadContext = useCallback(() => {
    setLoading(true);
    setLoadError("");
    return fetchEncashmentContext(selectedEmployeeId || undefined)
      .then(setContext)
      .catch((error) => setLoadError(error.message))
      .finally(() => setLoading(false));
  }, [selectedEmployeeId]);

  const loadHistory = useCallback((offset = 0) => {
    return fetchEncashmentRequests({
      status: status || undefined,
      source: source || undefined,
      employee_id: selectedEmployeeId || undefined,
      limit: 20,
      offset,
    })
      .then(setHistory)
      .catch((error) => setMessage(error.message));
  }, [status, source, selectedEmployeeId]);

  useEffect(() => { loadContext(); }, [loadContext]);
  useEffect(() => { loadHistory(0); }, [loadHistory]);

  useEffect(() => {
    if (!context?.can_request) {
      setQuote(null);
      return undefined;
    }
    const qty = Number(daysToEncash);
    if (!daysToEncash || Number.isNaN(qty) || qty <= 0) {
      setQuote(null);
      setQuoteError("");
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuoting(true);
      setQuoteError("");
      quoteEncashment(qty, context.employee_id)
        .then(setQuote)
        .catch((error) => {
          setQuote(null);
          setQuoteError(error.message);
        })
        .finally(() => setQuoting(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [daysToEncash, context]);

  const refresh = async () => {
    await loadContext();
    await loadHistory(history.offset || 0);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage("");
    try {
      const created = await submitEncashment(
        Number(daysToEncash),
        context?.can_act_for_employee ? selectedEmployeeId : undefined,
      );
      setDaysToEncash("");
      setQuote(null);
      const who = created?.employee_name || formatEmployeeLabel(selectedEmployee);
      setMessage(
        context?.can_act_for_employee
          ? (who ? `Encashment submitted for ${who}.` : "Encashment request submitted.")
          : "Request sent. It becomes a leave encashment after HR, admin, or super admin approval.",
      );
      await refresh();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const act = async (id, action) => {
    setBusyId(id);
    setMessage("");
    try {
      await action();
      setRejectFor(null);
      setRejectNotes("");
      await refresh();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusyId("");
    }
  };

  const settings = context?.settings;
  const hr = context?.role === "admin" || context?.role === "hr";
  const canAct = Boolean(context?.can_act_for_employee);
  const showBalance = Boolean(settings && context?.employee_id);
  const selectedLabel = formatEmployeeLabel(selectedEmployee);
  const canDecide = (row) => {
    if (context?.role === "admin") return row.status === "pending_manager" || row.status === "pending_hr";
    if (context?.role === "hr") return row.status === "pending_hr";
    if (context?.role === "manager") return row.status === "pending_manager";
    return false;
  };

  const employeePortal = !hr && !canAct;
  const showPager = (history.total || 0) > (history.limit || 20);
  const lastRun = runSummary || context?.last_year_end_run;
  const monthLabel = settings?.year_end_month_label || "the configured month";

  return (
    <div className="enc-page">
      {loading ? <p className="enc-muted">Loading…</p> : null}
      {loadError ? <p className="enc-alert bad" role="alert">{loadError}</p> : null}
      {message ? <p className={message.toLowerCase().includes("submitted") || message.toLowerCase().includes("finished") ? "enc-alert ok" : "enc-alert"} role="status">{message}</p> : null}

      {showBalance ? (
        <section className="enc-balance" aria-label="Earned leave encashment balance">
          <div className="is-main">
            <span>Available to encash</span>
            <strong>{days(context.eligible_available)}<em>days</em></strong>
          </div>
          <div>
            <span>Minimum balance</span>
            <strong>{days(settings.min_el_balance)}<em>days</em></strong>
          </div>
          <div>
            <span>Minimum request</span>
            <strong>{days(settings.min_days)}<em>days</em></strong>
          </div>
          <div>
            <span>Reserved</span>
            <strong>{days(context.reserved_days)}<em>days</em></strong>
          </div>
        </section>
      ) : null}

      {context?.blocked_reason && !canAct ? <p className="enc-note">{context.blocked_reason}</p> : null}

      {canAct || context?.can_request ? (
        <form className="enc-card" onSubmit={onSubmit}>
          {employeePortal ? (
            <div className="enc-request-head">
              <h2>Raise a request</h2>
              <p>HR, an admin, or a super admin approves it. Approval creates the leave encashment and sends it to payroll.</p>
            </div>
          ) : null}
          <div className="enc-request">
            {canAct ? (
              <label>
                Employee
                <EmployeeSelect
                  id="enc-employee"
                  hideLabel
                  loadingHint={false}
                  value={selectedEmployeeId}
                  emptyLabel="Select an employee"
                  selectClassName="enc-select"
                  onChange={(id, employee) => {
                    setSelectedEmployeeId(id || "");
                    setSelectedEmployee(employee || null);
                    setDaysToEncash("");
                    setQuote(null);
                    setQuoteError("");
                  }}
                />
              </label>
            ) : null}
            {context?.can_request ? (
              <>
                <label>
                  Days to encash
                  <input
                    type="number"
                    min={context.half_day_allowed ? "0.5" : "1"}
                    step={context.half_day_allowed ? "0.5" : "1"}
                    value={daysToEncash}
                    onChange={(event) => setDaysToEncash(event.target.value)}
                    required
                  />
                </label>
                <div className="enc-estimate">
                  <span>Estimated amount</span>
                  <strong>{quoting ? "Calculating…" : money(quote?.amount)}</strong>
                </div>
                <button className="enc-btn" type="submit" disabled={submitting || quoting || !quote?.amount || quote?.eligible === false}>
                  {submitting ? "Submitting…" : canAct ? "Encash for employee" : "Submit request"}
                </button>
              </>
            ) : null}
          </div>
          {canAct && !context?.employee_id && !context?.blocked_reason ? (
            <p className="enc-basis">Select an employee, then enter the days to encash. Use this when they ask HR or an admin to do it for them.</p>
          ) : null}
          {canAct && context?.blocked_reason ? <p className="enc-basis">{context.blocked_reason}</p> : null}
          {context?.can_request ? (
            <div className="enc-basis-wrap">
              <button
                type="button"
                className="enc-btn-ghost"
                aria-expanded={showBasis}
                onClick={() => setShowBasis((open) => !open)}
              >
                {showBasis ? "Hide calculation" : "How is this calculated?"}
              </button>
              {showBasis ? (
                <p className="enc-basis">
                  {canAct && selectedLabel ? `Encashing for ${selectedLabel}. ` : null}
                  {quoteError || quote?.basis_text || context.preview_basis || "Enter the days to see the estimate."}
                  {quote?.errors?.length ? <span className="enc-reason"> {quote.errors.join(" ")}</span> : null}
                </p>
              ) : null}
            </div>
          ) : null}
        </form>
      ) : null}

      <section className="enc-card" aria-labelledby="enc-history">
        <div className="enc-toolbar">
          <div>
            <h2 id="enc-history">{hr ? (selectedLabel ? selectedLabel : "All employees") : "Your requests"}</h2>
            {employeePortal ? <p>A request stays here until it is approved. Approval is what creates the encashment.</p> : null}
            {hr && settings?.year_end_enabled ? <p>Year-end runs in {monthLabel}. Each employee is processed once.</p> : null}
          </div>
          <div className="enc-toolbar-actions">
            {hr && settings?.year_end_enabled && !confirmRun ? (
              <button type="button" className="enc-btn" onClick={() => setConfirmRun(true)}>Run {monthLabel}</button>
            ) : null}
            <select aria-label="Approval status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              <option value="pending_manager">Pending manager</option>
              <option value="pending_hr">Pending HR</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
            {employeePortal ? null : (
              <select aria-label="Encashment source" value={source} onChange={(event) => setSource(event.target.value)}>
                <option value="">All sources</option>
                <option value="employee_request">Employee request</option>
                <option value="on_behalf">Done by HR or admin</option>
                <option value="year_end">Year-end</option>
              </select>
            )}
          </div>
        </div>

        {confirmRun ? (
          <div className="enc-confirm">
            <p>Process eligible earned leave for {monthLabel}? Employees already processed this leave year are skipped.</p>
            <div className="enc-actions">
              <button type="button" className="enc-btn" disabled={busyId === "year-end"} onClick={() => act("year-end", async () => {
                const summary = await runYearEndEncashment();
                setRunSummary(summary);
                setConfirmRun(false);
                setMessage("Year-end processing finished.");
              })}>
                {busyId === "year-end" ? "Running…" : "Process now"}
              </button>
              <button type="button" className="enc-btn-ghost" onClick={() => setConfirmRun(false)}>Cancel</button>
            </div>
          </div>
        ) : null}

        {lastRun ? (
          <p className="enc-run">
            Last run {lastRun.ran_on || "today"}: {lastRun.created || 0} created, {lastRun.skipped || 0} skipped, {lastRun.failed || 0} failed, {lastRun.duplicate || 0} already done.
          </p>
        ) : null}

        {history.items?.length ? (
          <div className="enc-list">
            {history.items.map((row) => {
              const notice = rowNotice(row);
              return (
              <article className="enc-item" key={row.encashment_id}>
                <div className="enc-item-main">
                  <strong>{row.employee_name || "Employee"}</strong>
                  <span>{days(row.days)} days</span>
                  <span>{money(row.approved_amount ?? row.estimated_amount)}</span>
                  <span>{row.requested_at ? new Date(row.requested_at).toLocaleDateString() : "—"}</span>
                  <span className="enc-pills">
                    <span className={`enc-pill ${row.status}`}>{employeePortal ? requestStatus(row) : label(row.status)}</span>
                    <span className={`enc-pill ${row.payroll_status}`}>{payrollLabel(row.payroll_status)}</span>
                  </span>
                </div>
                {notice ? <p className={notice.tone === "warn" ? "enc-warn enc-warn-inline" : "enc-reason"}>{notice.text}</p> : null}
                <div className="enc-actions">
                  {canDecide(row) ? (
                    <>
                      <button type="button" className="enc-btn" disabled={busyId === row.encashment_id} onClick={() => act(row.encashment_id, () => decideEncashment(row.encashment_id, { stage: row.status === "pending_hr" ? "hr" : "manager", decision: "approve" }))}>
                        Approve and encash
                      </button>
                      <button type="button" className="enc-btn-danger" onClick={() => { setRejectFor(row); setRejectNotes(""); }}>Reject</button>
                    </>
                  ) : null}
                  {row.status.startsWith("pending") && context?.employee_id === row.employee_id ? (
                    <button type="button" className="enc-btn-ghost" disabled={busyId === row.encashment_id} onClick={() => act(row.encashment_id, () => cancelEncashment(row.encashment_id))}>
                      Cancel
                    </button>
                  ) : null}
                  {hr && row.status === "approved" && !row.balance_deducted && !["pending", "processed"].includes(row.payroll_status) ? (
                    <button type="button" className="enc-btn-ghost" disabled={busyId === row.encashment_id} onClick={() => act(row.encashment_id, () => cancelEncashment(row.encashment_id))}>
                      Release
                    </button>
                  ) : null}
                  {hr && row.status === "approved" && ["queued", "setup_required", "failed"].includes(row.payroll_status) ? (
                    <button type="button" className="enc-btn-ghost" disabled={busyId === row.encashment_id} onClick={() => act(row.encashment_id, () => retryEncashmentPayroll(row.encashment_id))}>
                      Retry payroll
                    </button>
                  ) : null}
                </div>
                {rejectFor?.encashment_id === row.encashment_id ? (
                  <div className="enc-reject-row">
                    <input className="enc-reject" aria-label="Rejection reason" placeholder="Reason for rejection" value={rejectNotes} onChange={(event) => setRejectNotes(event.target.value)} />
                    <button type="button" className="enc-btn-danger" disabled={!rejectNotes.trim() || busyId === row.encashment_id} onClick={() => act(row.encashment_id, () => decideEncashment(row.encashment_id, { stage: row.status === "pending_hr" ? "hr" : "manager", decision: "reject", notes: rejectNotes.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : null}
              </article>
              );
            })}
          </div>
        ) : (
          <p className="enc-empty">No encashment records yet.</p>
        )}

        {history.total > 0 ? (
          <div className="enc-actions enc-pager">
            <span className="enc-page-count">
              {Math.min((history.offset || 0) + 1, history.total)}–{Math.min((history.offset || 0) + (history.items?.length || 0), history.total)} of {history.total}
            </span>
            {showPager ? (
              <>
                <button type="button" className="enc-btn-ghost" disabled={!history.offset} onClick={() => loadHistory(Math.max(0, (history.offset || 0) - (history.limit || 20)))}>Previous</button>
                <button type="button" className="enc-btn-ghost" disabled={(history.offset || 0) + (history.items?.length || 0) >= (history.total || 0)} onClick={() => loadHistory((history.offset || 0) + (history.limit || 20))}>Next</button>
              </>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
